/**
 * API calls for the dynamic report card module.
 * Backend: backend/app/routes/reportcard.routes.js  (+ grade master in result.routes.js)
 */
import * as constants from "../../constants/CONSTANT";
import { getAxios } from "../../utils/helper";

const RC = `${constants.API_BASE_URL}/api/reportcard`;

const call = async (fn) => {
  const { data } = await fn(getAxios());
  return data;
};

// 404 "no records" from older endpoints -> empty list instead of an error
const listOrEmpty = async (fn, key) => {
  try {
    const data = await call(fn);
    return data?.[key] || [];
  } catch (e) {
    if (e?.response?.status === 404) return [];
    throw e;
  }
};

export const errorMessage = (e, fallback = "Something went wrong") =>
  e?.response?.data?.message ||
  e?.response?.data?.errors?.[0]?.msg ||
  (typeof e?.response?.data?.errors === "string" ? e.response.data.errors : null) ||
  e?.message ||
  fallback;

const master = (path) => ({
  list: () => listOrEmpty((ax) => ax.get(`${RC}/${path}`), "records"),
  create: (rec) => call((ax) => ax.post(`${RC}/${path}`, rec)),
  update: (id, rec) => call((ax) => ax.put(`${RC}/${path}/${id}`, rec)),
  remove: (id) => call((ax) => ax.delete(`${RC}/${path}/${id}`)),
  getClasses: (id) => call((ax) => ax.get(`${RC}/${path}/${id}/classes`)).then((d) => d.class_ids || []),
  setClasses: (id, class_ids) => call((ax) => ax.put(`${RC}/${path}/${id}/classes`, { class_ids })),
});

const reportCardApi = {
  examGroups: master("exam-groups"),
  coScholasticSubjects: master("co-scholastic-subjects"),
  footerFields: master("footer-fields"),

  // grade master (existing /api/results/grade endpoints, extended)
  grades: {
    list: (params = {}) =>
      listOrEmpty((ax) => ax.get(`${constants.API_BASE_URL}/api/results/grade`, { params }), "grades"),
    create: (rec) => call((ax) => ax.post(`${constants.API_BASE_URL}/api/results/grade`, rec)),
    update: (id, rec) => call((ax) => ax.put(`${constants.API_BASE_URL}/api/results/grade/${id}`, rec)),
    remove: (id) => call((ax) => ax.delete(`${constants.API_BASE_URL}/api/results/grade/${id}`)),
  },

  // exam types (Theory, Practical, Notebook, Project …) – /api/exam-types
  examTypes: {
    list: (params = {}) => call((ax) => ax.get(`${constants.API_BASE_URL}/api/exam-types`, { params })).then((d) => d?.result || []),
    create: (rec) => call((ax) => ax.post(`${constants.API_BASE_URL}/api/exam-types`, rec)),
    update: (id, rec) => call((ax) => ax.put(`${constants.API_BASE_URL}/api/exam-types/${id}`, rec)),
    remove: (id) => call((ax) => ax.delete(`${constants.API_BASE_URL}/api/exam-types/${id}`)),
  },

  // classes (existing endpoint)
  activeClasses: () =>
    listOrEmpty((ax) => ax.get(`${constants.API_BASE_URL}/api/classes/active`), "result"),

  subjectLayout: {
    get: (class_id) => listOrEmpty((ax) => ax.get(`${RC}/subject-layout`, { params: { class_id } }), "records"),
    save: (rows) => call((ax) => ax.put(`${RC}/subject-layout`, { rows })),
  },

  // card layout designer (class_id "default" = School Default)
  layout: {
    get: (class_id) => call((ax) => ax.get(`${RC}/layout`, { params: { class_id: class_id || "default" } })),
    save: (class_id, layout) => call((ax) => ax.put(`${RC}/layout`, { class_id: class_id || "default", layout })),
    reset: (class_id) => call((ax) => ax.delete(`${RC}/layout`, { params: { class_id: class_id || "default" } })),
  },

  // lookups
  sections: (class_id) => listOrEmpty((ax) => ax.get(`${RC}/sections`, { params: { class_id } }), "records"),
  examTitles: (sessionid) => listOrEmpty((ax) => ax.get(`${RC}/exam-titles`, { params: { sessionid } }), "records"),
  gradeOptions: (class_id, type = "CO_SCHOLASTIC") =>
    listOrEmpty((ax) => ax.get(`${RC}/grade-options`, { params: { class_id, type } }), "records"),

  // entry screens + report
  coScholasticResults: {
    get: (params) => call((ax) => ax.get(`${RC}/co-scholastic-results`, { params })),
    save: (body) => call((ax) => ax.post(`${RC}/co-scholastic-results`, body)),
  },
  footerValues: {
    get: (params) => call((ax) => ax.get(`${RC}/footer-values`, { params })),
    save: (body) => call((ax) => ax.post(`${RC}/footer-values`, body)),
  },
  build: (admissionId, params) => call((ax) => ax.get(`${RC}/build/${admissionId}`, { params })),
  published: (admissionId, params) => call((ax) => ax.get(`${RC}/published/${admissionId}`, { params })),
  publishStatus: (params) => call((ax) => ax.get(`${RC}/publish-status`, { params })),
  publish: (body) => call((ax) => ax.post(`${RC}/publish`, body)),
  withdraw: (body) => call((ax) => ax.post(`${RC}/withdraw`, body)),
  updateRollNo: (student_addmission_id, roll_no) =>
    call((ax) => ax.post(`${RC}/student-roll-no`, { student_addmission_id, roll_no })),
};

export default reportCardApi;
